import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';

function NavLink({ to, label, active }) {
  const [hovered, setHovered] = useState(false);
  return (
    <Link
      to={to}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        fontSize: 13.5,
        fontWeight: active ? 600 : 500,
        textDecoration: 'none',
        padding: '8px 16px',
        borderRadius: 100,
        color: active
          ? 'var(--tandm-accent-deep)'
          : hovered
            ? 'var(--tandm-ink)'
            : 'var(--tandm-body)',
        background: active
          ? 'var(--tandm-accent-subtle)'
          : hovered
            ? 'var(--tandm-line-soft)'
            : 'transparent',
        transition: 'color 0.18s, background 0.18s',
        whiteSpace: 'nowrap',
        fontFamily: 'var(--tandm-font-sans)',
      }}
    >
      {label}
    </Link>
  );
}

function DropdownItem({ as: Tag = 'div', style, children, ...props }) {
  const [hovered, setHovered] = useState(false);
  return (
    <Tag
      {...props}
      style={{ ...style, background: hovered ? 'var(--tandm-line-soft)' : 'none' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
    </Tag>
  );
}

/**
 * Offer Desk nav for the logged-in product shell:
 * Dashboard, Internships, Applications, Profile, and job detail.
 * Admin keeps its own chrome (out of this pass).
 */
export default function OfferDeskNav() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => {
    if (path === '/jobs') return location.pathname === '/jobs' || location.pathname.startsWith('/jobs/');
    return location.pathname === path;
  };

  const navLinks = [
    { to: '/dashboard', label: 'Dashboard' },
    { to: '/jobs', label: 'Internships' },
    { to: '/applications', label: 'Applications' },
    ...(user?.role === 'admin' ? [{ to: '/admin', label: 'Admin' }] : []),
  ];

  const ddItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    padding: '9px 12px',
    fontSize: 13,
    fontWeight: 500,
    color: 'var(--tandm-ink-soft)',
    textDecoration: 'none',
    borderRadius: 10,
    fontFamily: 'var(--tandm-font-sans)',
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    width: '100%',
    textAlign: 'left',
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        padding: '8px 10px 8px 16px',
        background: 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid var(--tandm-line)',
        borderRadius: 100,
        boxShadow: 'var(--tandm-shadow-soft)',
        fontFamily: 'var(--tandm-font-sans)',
        maxWidth: 'calc(100vw - 24px)',
      }}
    >
      <Link
        to="/jobs"
        style={{
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          marginRight: 10,
          flexShrink: 0,
          gap: 8,
        }}
      >
        <img
          src="/tandm-logo-primary.png"
          alt="Tandm"
          style={{ height: 28, width: 'auto', display: 'block' }}
        />
      </Link>

      {user && (
        <div style={{ width: 1, height: 22, background: 'var(--tandm-line)', marginRight: 2 }} />
      )}

      {user && navLinks.map((link) => (
        <NavLink key={link.to} to={link.to} label={link.label} active={isActive(link.to)} />
      ))}

      {user && (
        <div style={{ width: 1, height: 22, background: 'var(--tandm-line)', margin: '0 4px' }} />
      )}

      {user && (
        <div style={{ position: 'relative' }} ref={dropdownRef}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setDropdownOpen((o) => !o);
            }}
            title={`${user.first_name} ${user.last_name}`}
            style={{
              width: 36,
              height: 36,
              flexShrink: 0,
              background: 'var(--tandm-gradient-cta)',
              borderRadius: '50%',
              border: '2px solid var(--tandm-accent-subtle)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: 13,
              fontWeight: 700,
              fontFamily: 'var(--tandm-font-sans)',
            }}
          >
            {user.first_name?.charAt(0).toUpperCase()}
          </button>

          {dropdownOpen && (
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.15 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'absolute',
                top: 'calc(100% + 12px)',
                right: 0,
                background: 'var(--tandm-surface)',
                border: '1px solid var(--tandm-line)',
                borderRadius: 16,
                padding: 6,
                minWidth: 210,
                boxShadow: 'var(--tandm-shadow-lift)',
                zIndex: 300,
              }}
            >
              <div style={{ padding: '10px 14px' }}>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--tandm-ink)' }}>
                  {user.first_name} {user.last_name}
                </p>
                <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--tandm-muted)' }}>
                  {user.email}
                </p>
              </div>
              <div style={{ height: 1, background: 'var(--tandm-line-soft)', margin: '4px 0' }} />
              {[
                {
                  to: '/profile',
                  label: 'My Profile',
                  icon: (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  ),
                },
                {
                  to: '/applications',
                  label: 'Applications',
                  icon: (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  ),
                },
              ].map(({ to, label, icon }) => (
                <DropdownItem
                  key={to}
                  as={Link}
                  to={to}
                  onClick={() => setDropdownOpen(false)}
                  style={ddItemStyle}
                >
                  <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
                    {icon}
                  </svg>
                  {label}
                </DropdownItem>
              ))}
              <div style={{ height: 1, background: 'var(--tandm-line-soft)', margin: '4px 0' }} />
              <DropdownItem
                as="button"
                onClick={handleLogout}
                style={{ ...ddItemStyle, color: 'var(--tandm-danger-fg)' }}
              >
                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
                Log out
              </DropdownItem>
            </motion.div>
          )}
        </div>
      )}
    </div>
  );
}
